import Cookies from 'js-cookie';

/**
 * #1825 — estado do consentimento para as tags de analise (GA4 e Microsoft Clarity).
 *
 * Tres estados, e o terceiro importa: `granted`, `denied` e **sem decisao** (cookie ausente).
 * "Ainda nao decidiu" nao e "recusou" — e o que faz o banner continuar perguntando para quem
 * deixou a caixa do cadastro desmarcada.
 *
 * O cookie e da plataforma e so dela: host-only, sem `domain`. A landing pede o consentimento
 * dela separadamente, e compartilhar a decisao entre os dois dominios seria decidir por quem
 * nunca foi perguntado.
 *
 * Quem injeta as tags e o carregador inline do layout (`vueapp.html.erb`), nao este arquivo:
 * aqui so se grava a decisao e se chama `window.flipdeskAnalytics.load()`.
 */

export const ANALYTICS_CONSENT_COOKIE = 'fd_analytics_consent';
/**
 * O instante da decisao, em cookie companheiro. Mora separado de proposito: o carregador
 * inline do layout compara o valor de `fd_analytics_consent` com `granted` em string crua, e
 * qualquer coisa a mais dentro dele quebraria a comparacao.
 *
 * Sem data nao da para reconciliar nada: cookie e registro podem discordar (duas abas, dois
 * dispositivos, revogacao no perfil de um deles) e "quem venceu" so se responde por tempo.
 */
export const ANALYTICS_CONSENT_AT_COOKIE = 'fd_analytics_consent_at';
export const CONSENT_GRANTED = 'granted';
export const CONSENT_DENIED = 'denied';
const CONSENT_EXPIRES_IN_DAYS = 365;
const CONSENT_EXPIRES_IN_MS = CONSENT_EXPIRES_IN_DAYS * 24 * 60 * 60 * 1000;

/**
 * Usado quando a instalacao nao configurou `privacyURL`. O texto do cadastro ja traz esta URL
 * embutida no proprio `<a>` da traducao, e substitui-la por vazio deixaria um `href` com
 * `undefined` dentro e a frase "Saiba mais na" sem link nenhum.
 */
export const DEFAULT_PRIVACY_URL = 'https://www.flipdesk.com.br/privacy';

// O que `recordAnalyticsConsent` devolve. Nao existe "gravado": a action `updateUISettings`
// engole o erro dela, entao o maximo que daqui se sabe e que o despacho saiu sem levantar.
export const RECORD_DISPATCHED = 'dispatched';
export const RECORD_SKIPPED = 'skipped';
export const RECORD_FAILED = 'failed';

// O que `syncAnalyticsConsent` devolve.
export const SYNC_IN_SYNC = 'in-sync';
export const SYNC_RESTORED = 'restored';
export const SYNC_REALIGNED = 'realigned';
export const SYNC_RECORD_DISPATCHED = 'record-dispatched';

// Os cookies que as duas ferramentas criam, mais o `fd_vid` da landing, que a politica publicada
// lista entre os cookies de medicao (secao 6.2). `_ga_<ID>` e por propriedade, entao o nome
// exato so da para saber lendo o que esta no navegador.
const ANALYTICS_COOKIE_NAMES = ['_ga', '_clck', '_clsk', 'fd_vid'];
const ANALYTICS_COOKIE_PREFIX = '_ga_';

export const getAnalyticsConsent = () => {
  const value = Cookies.get(ANALYTICS_CONSENT_COOKIE);
  return value === CONSENT_GRANTED || value === CONSENT_DENIED ? value : null;
};

export const hasAnalyticsConsentDecision = () => getAnalyticsConsent() !== null;

export const isAnalyticsConsentGranted = () =>
  getAnalyticsConsent() === CONSENT_GRANTED;

/**
 * A data da decisao, como ela foi gravada. String ISO 8601 ou `null` — quem decidiu antes
 * deste deploy tem o cookie da decisao e nao tem o companheiro.
 */
export const getAnalyticsConsentAt = () =>
  Cookies.get(ANALYTICS_CONSENT_AT_COOKIE) || null;

// O prazo conta da DECISAO, e nao de quando o cookie foi escrito: restaurar num dispositivo novo
// uma decisao de 300 dias atras nao pode dar a ela mais um ano.
const consentCookieOptions = decidedAt => ({
  expires: new Date(Date.parse(decidedAt) + CONSENT_EXPIRES_IN_MS),
  path: '/',
  sameSite: 'Lax',
  secure:
    typeof window !== 'undefined' && window.location.protocol === 'https:',
});

export const setAnalyticsConsent = (status, decidedAt) => {
  const at = Number.isNaN(Date.parse(decidedAt))
    ? new Date().toISOString()
    : decidedAt;
  const options = consentCookieOptions(at);

  Cookies.set(ANALYTICS_CONSENT_COOKIE, status, options);
  Cookies.set(ANALYTICS_CONSENT_AT_COOKIE, at, options);

  return status;
};

/**
 * Apaga a decisao DESTE navegador. Chamado no logout: computador compartilhado e caso comum
 * no atendimento, e a resposta de quem saiu nao pode valer para quem entra depois. Nao mexe
 * nos cookies das ferramentas — quem os apaga e a revogacao, que e outra coisa.
 */
export const clearAnalyticsConsentDecision = () => {
  Cookies.remove(ANALYTICS_CONSENT_COOKIE, { path: '/' });
  Cookies.remove(ANALYTICS_CONSENT_AT_COOKIE, { path: '/' });
};

/**
 * Manda o carregador do layout injetar as tags agora. Idempotente do lado de la, e no-op
 * quando nao ha tag configurada (self-hosted, dev) ou quando o consentimento nao foi dado.
 */
export const loadAnalyticsTags = () => {
  const loader =
    typeof window === 'undefined' ? undefined : window.flipdeskAnalytics;

  return Boolean(loader && loader.load && loader.load());
};

export const isAnalyticsConfigured = () =>
  typeof window !== 'undefined' &&
  Boolean(
    window.globalConfig &&
      (window.globalConfig.GOOGLE_TAG || window.globalConfig.MS_CLARITY_TOKEN)
  );

/**
 * Os dominios em que vale a pena tentar apagar um cookie de analise: o proprio host e cada
 * dominio pai ate o dominio registravel.
 *
 * O `_ga` que interessa apagar e o da LANDING, gravado em `.flipdesk.com.br` — apagar so no
 * host deixaria o identificador vivo e ele voltaria no proximo aceite.
 *
 * Tenta TODOS os pais, do host ate o penultimo nivel. Nao ha heuristica de ccTLD aqui, e e de
 * proposito: adivinhar onde fica o dominio registravel sem a Public Suffix List erra, e errava
 * para o lado caro — num `app.cliente.io` a regra antiga via "duas partes, TLD de duas letras"
 * e pulava `.cliente.io`, deixando o `_ga` vivo exatamente onde a revogacao precisava apaga-lo.
 *
 * Tentar um sufixo publico e inofensivo: o navegador ignora `domain=.com.br`, entao a remocao
 * a mais e no-op. O TLD sozinho fica de fora so por nao haver caso em que ele ajude.
 */
export const analyticsCookieDomains = hostname => {
  const parts = String(hostname || '')
    .split('.')
    .filter(Boolean);
  const domains = [undefined];

  // Comeca no proprio host: cookie gravado com `Domain=<host>` nao sai pela remocao host-only.
  for (let index = 0; index < parts.length - 1; index += 1) {
    domains.push(`.${parts.slice(index).join('.')}`);
  }

  return domains;
};

/**
 * Desliga as tags ja injetadas nesta pagina, antes de apagar os cookies. Sem isso o `gtag.js` e
 * o `clarity.js` ainda vivos podem regravar `_ga`/`_clck` entre a limpeza e o reload.
 * `ga-disable-<id>` e o opt-out documentado do Google; `clarity('consent', false)` passa o
 * Clarity para o modo sem cookie (conferido no clarity.js 0.8.75).
 */
export const disableAnalyticsTags = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const googleTag = window.flipdeskAnalytics?.googleTag;
  if (googleTag) {
    window[`ga-disable-${googleTag}`] = true;
  }
  if (typeof window.clarity === 'function') {
    window.clarity('consent', false);
  }
};

/**
 * Apaga os cookies das duas ferramentas. Nao e enfeite da revogacao: sem isso o `_ga` continua
 * no navegador e o mesmo identificador volta a ser usado se a pessoa aceitar de novo — a
 * revogacao teria apagado a decisao, nao o identificador.
 */
export const clearAnalyticsCookies = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  const existing = Object.keys(Cookies.get() || {});
  const names = [
    ...new Set([
      ...ANALYTICS_COOKIE_NAMES,
      ...existing.filter(name => name.startsWith(ANALYTICS_COOKIE_PREFIX)),
    ]),
  ];
  const domains = analyticsCookieDomains(window.location.hostname);

  names.forEach(name => {
    domains.forEach(domain => {
      Cookies.remove(name, domain ? { path: '/', domain } : { path: '/' });
    });
  });

  return names;
};

export const ANALYTICS_CONSENT_UI_SETTING = 'analytics_consent';

const decidedAtTime = value => {
  if (!value) {
    return null;
  }

  const time = Date.parse(value);
  return Number.isNaN(time) ? null : time;
};

/**
 * A decisao de quem esta logado tambem fica no `ui_settings` do usuario, com data: o cookie
 * prova a decisao NESTE navegador, e o registro prova que ela aconteceu. Sem ele, trocar de
 * computador apagaria o consentimento e a pessoa seria perguntada de novo a cada dispositivo.
 *
 * No app v3 (`/app/login`, `/app/register`) nao ha usuario nem a action: tudo aqui vira no-op.
 *
 * Esta funcao devolve o registro inteiro: estado e data. `null` quando nao ha registro, quando
 * o estado e desconhecido, e quando a decisao VENCEU.
 *
 * Vencer importa: o cookie dura um ano e, passado esse prazo, o banner volta a perguntar.
 * Sem o mesmo prazo do lado do registro, a decisao de 2026 restauraria o cookie para sempre e
 * ninguem seria perguntado de novo. Registro sem data e de antes deste deploy e nao vence —
 * nao da para datar o que nunca foi datado.
 */
export const readAnalyticsConsentRecord = store => {
  const recorded =
    store?.getters?.getUISettings?.[ANALYTICS_CONSENT_UI_SETTING];
  const status = recorded?.status;

  if (status !== CONSENT_GRANTED && status !== CONSENT_DENIED) {
    return null;
  }

  const decidedAt = recorded?.decided_at || null;
  const time = decidedAtTime(decidedAt);
  if (time !== null && Date.now() - time > CONSENT_EXPIRES_IN_MS) {
    return null;
  }

  return { status, decidedAt, time };
};

export const readRecordedAnalyticsConsent = store =>
  readAnalyticsConsentRecord(store)?.status || null;

/**
 * Despacha a gravacao do registro. O retorno NAO diz "gravado": a action `updateUISettings`
 * trata o erro dela por dentro e resolve igual quando o PUT falha, entao daqui so se sabe que
 * o despacho saiu sem levantar. Quem chama nao pode anunciar sucesso com base nisto.
 *
 * A data vem da decisao (o cookie companheiro), e nao de "agora": no boot do dashboard o
 * registro pode estar sendo criado por um aceite que aconteceu no cadastro, dias antes.
 */
export const recordAnalyticsConsent = async (store, status, decidedAt) => {
  if (!store?.getters?.getCurrentUser?.id) {
    return RECORD_SKIPPED;
  }

  try {
    await store.dispatch('updateUISettings', {
      uiSettings: {
        ...(store.getters.getUISettings || {}),
        [ANALYTICS_CONSENT_UI_SETTING]: {
          status,
          decided_at:
            decidedAt || getAnalyticsConsentAt() || new Date().toISOString(),
        },
      },
    });

    return RECORD_DISPATCHED;
  } catch (error) {
    // A decisao ja vale neste navegador pelo cookie; perder o registro nao pode derrubar a tela.
    return RECORD_FAILED;
  }
};

/**
 * Traz o registro para o navegador. `previousCookie` e o que havia aqui antes — so ele
 * distingue "nunca decidiu neste navegador" de "este navegador estava medindo".
 */
const applyRecordToBrowser = (record, { previousCookie = null } = {}) => {
  setAnalyticsConsent(record.status, record.decidedAt || undefined);

  if (record.status === CONSENT_GRANTED) {
    loadAnalyticsTags();
    return;
  }

  if (previousCookie !== CONSENT_GRANTED) {
    return;
  }

  // Recusa vinda de outro dispositivo com as tags JA injetadas nesta pagina: o carregador do
  // layout rodou no boot, quando o cookie ainda dizia `granted`, e script carregado nao se
  // desinjeta. So a recarga o descarta. O cookie ja esta `denied` antes dela, entao o
  // carregador nao sobe de novo e nao ha laco.
  disableAnalyticsTags();
  clearAnalyticsCookies();
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
};

/**
 * Quando as duas superficies discordam, vence a decisao MAIS RECENTE.
 *
 * Faltando data de um dos lados nao da para saber quem decidiu depois, e chutar errado aqui
 * custa medir quem recusou. Nesse caso vence `denied`, o mais restritivo — e como os dois
 * lados discordam, exatamente um deles e `denied`.
 */
const recordWinsOver = (cookie, record) => {
  const cookieTime = decidedAtTime(getAnalyticsConsentAt());

  if (cookieTime === null || record.time === null) {
    return record.status === CONSENT_DENIED;
  }

  return record.time > cookieTime;
};

/**
 * Casa as duas superficies no boot do dashboard, nos tres casos:
 *
 *   - cookie sem registro: o aceite foi dado no CADASTRO, antes de existir sessao. Sem isto ele
 *     nunca viraria prova e sumiria na troca de dispositivo;
 *   - registro sem cookie: dispositivo novo (ou cookie expirado) de quem ja decidiu. Perguntar
 *     de novo seria ignorar a resposta que a pessoa ja deu;
 *   - os dois, discordando: alguem decidiu de novo em outro lugar. Ignorar o conflito (era o
 *     que se fazia) deixava a revogacao feita no celular sem efeito nenhum no computador, e a
 *     politica publicada promete que a revogacao interrompe a coleta.
 */
export const syncAnalyticsConsent = async store => {
  const cookie = getAnalyticsConsent();
  const record = readAnalyticsConsentRecord(store);

  if (cookie && !record) {
    await recordAnalyticsConsent(store, cookie);
    return SYNC_RECORD_DISPATCHED;
  }

  if (!cookie && record) {
    applyRecordToBrowser(record);
    return SYNC_RESTORED;
  }

  if (!cookie || !record || cookie === record.status) {
    return SYNC_IN_SYNC;
  }

  if (recordWinsOver(cookie, record)) {
    applyRecordToBrowser(record, { previousCookie: cookie });
    return SYNC_REALIGNED;
  }

  await recordAnalyticsConsent(store, cookie);
  return SYNC_RECORD_DISPATCHED;
};
